if (!customElements.get('recycling-desktop-layout')) {
  customElements.define('recycling-desktop-layout', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.media = window.matchMedia('(min-width: 768px)');
      this.groups = [];
      this.addEventListener('play', event => {
        if (event.target.closest('[data-recycling-block][hidden]')) event.target.pause();
      }, { capture: true, signal: this.controller.signal });
      this.update = () => this.media.matches ? this.build() : this.restore();
      this.media.addEventListener('change', this.update);
      this.addEventListener('shopify:block:select', event => {
        const block = event.target.closest('[data-recycling-block]') || event.target.querySelector('[data-recycling-block]');
        const group = this.groups.find(item => item.slides.includes(block));
        if (group) this.show(group, group.slides.indexOf(block));
      }, { signal: this.controller.signal });
      this.addEventListener('click', event => {
        const productPage = event.target.closest('[data-program-page]');
        if (productPage) {
          const dots = [...productPage.closest('.recycling-program-frame').querySelectorAll('[data-rpf-dot]')];
          const index = dots.findIndex(dot => dot.getAttribute('aria-current') === 'true');
          if (dots.length) dots[(index + Number(productPage.dataset.programPage) + dots.length) % dots.length].click();
          return;
        }
        if (!event.target.closest('[data-recycling-scroll]')) return;
        const target = this.querySelector('.recycling-program-frame');
        target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal: this.controller.signal });
      this.update();
    }

    disconnectedCallback() {
      this.media?.removeEventListener('change', this.update);
      this.controller?.abort();
      this.restore();
      this.controller = null;
    }

    build() {
      if (this.groups.length) return;
      const buckets = new Map();
      [...this.children].forEach(slide => {
        const key = slide.dataset.desktopGroup;
        if (!key || key === 'none' || slide.dataset.desktopHidden === 'true') return;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(slide);
      });
      buckets.forEach((slides, key) => {
        if (slides.length < 2) return;
        const shell = document.createElement('section');
        shell.className = 'recycling-pagination';
        shell.dataset.group = key;
        shell.setAttribute('aria-roledescription', 'carousel');
        shell.setAttribute('aria-label', { hero: 'Beetles Loves Earth', rewards: 'Recycling rewards', process: 'Recycling process and support' }[key] || key);
        slides[0].before(shell);
        const markers = slides.map(slide => {
          const marker = document.createComment('recycling-block-position');
          slide.before(marker);
          shell.append(slide);
          return marker;
        });
        const group = { shell, slides, markers, index: 0, dots: [] };
        const button = (attribute, label, callback) => {
          const el = document.createElement('button');
          el.type = 'button';
          el.setAttribute(attribute, '');
          el.setAttribute('aria-label', label);
          el.addEventListener('click', callback);
          return el;
        };
        ['prev', 'next'].forEach(direction => {
          const el = button(`data-${direction}`, direction === 'prev' ? 'Previous slide' : 'Next slide', () => this.show(group, group.index + (direction === 'prev' ? -1 : 1)));
          el.className = `recycling-pagination__arrow recycling-pagination__arrow--${direction}`;
          const icon = document.getElementById(this.dataset.arrowTemplate)?.content.firstElementChild;
          if (icon) el.append(icon.cloneNode(true));
          shell.append(el);
        });
        const dots = document.createElement('div');
        dots.className = 'recycling-pagination__dots';
        dots.setAttribute('aria-label', 'Choose slide');
        slides.forEach((slide, index) => {
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`);
          const dot = button('data-page', `Show slide ${index + 1} of ${slides.length}`, () => this.show(group, index));
          dot.dataset.page = index;
          if (slide.id) dot.setAttribute('aria-controls', slide.id);
          dots.append(dot);
          group.dots.push(dot);
        });
        shell.append(dots);
        shell.addEventListener('keydown', event => {
          if (!event.target.closest('.recycling-pagination__arrow, .recycling-pagination__dots')) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.show(group, group.index + (event.key === 'ArrowLeft' ? -1 : 1));
          group.dots[group.index].focus();
        });
        this.groups.push(group);
        this.show(group, 0);
      });
    }

    show(group, index) {
      group.index = (index + group.slides.length) % group.slides.length;
      group.slides.forEach((slide, i) => {
        const active = i === group.index;
        if (!active) {
          if (slide.contains(document.activeElement)) group.dots[group.index].focus();
          slide.querySelectorAll('video, audio').forEach(media => media.pause());
          slide.querySelectorAll('iframe').forEach(frame => {
            try {
              const url = new URL(frame.src);
              if (/^(www\.)?youtube(-nocookie)?\.com$/.test(url.hostname)) {
                frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), url.origin);
              } else if (url.hostname === 'player.vimeo.com') {
                frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), url.origin);
              }
            } catch (_) {}
          });
        }
        slide.hidden = !active;
        slide.inert = !active;
        group.dots[i].setAttribute('aria-current', String(active));
      });
      group.shell.dataset.tone = group.slides[group.index].dataset.desktopTone || 'light';
    }

    restore() {
      this.groups?.forEach(({ shell, slides, markers }) => {
        slides.forEach((slide, index) => {
          slide.hidden = false;
          slide.inert = false;
          ['role', 'aria-roledescription', 'aria-label'].forEach(attribute => slide.removeAttribute(attribute));
          markers[index].replaceWith(slide);
        });
        shell.remove();
      });
      this.groups = [];
    }
  });
}
